import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Pessoa, { PessoasDisponibilidadeResponse } from '../../../model/Pessoa';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../../utils/breadcrumbUtils';
import { ComposicaoDisponibilidade, ComposicaoTemplates, PapelComposicao, VinculoQuantidade, TemplateComposicao } from '../../../model/banca/ComposicaoDisponibilidade';
import TemplateSelector from '../../../components/TemplateSelector';
import { ComposicaoDisponibilidadeViewModel } from '../../../viewmodel/banca/ComposicaoDisponibilidadeViewModel';
import PessoaViewModel from '../../../viewmodel/PessoaViewModel';
import { normalizarComposicao } from '../../../utils/papelUtils';
import { CategoriaEdital, formatarCategoria } from '../../../model/processo/Categoria';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { MembrosBancaViewModel } from '../../../viewmodel/banca/MembrosBancaViewModel';
import { PapelNome } from '../../../model/banca/MembroBanca';
import { BancaUtils, TipoBancaNome } from '../../../model/banca/Banca';
import ModernStepper, { StepperNavigation } from './components/ModernStepper';
import Joyride, { CallBackProps, STATUS } from 'react-joyride';

interface SelecoesEstado {
    titulares: Record<string, Pessoa[]>;
    papeis: Record<string, Pessoa[]>;
}

const INT_MAX = 2147483647;
const LIMITE_CONSULTA_POR_VINCULO = 150;

const sanitizeMax = (valor: number): number => (valor >= INT_MAX ? Number.POSITIVE_INFINITY : valor);

const formatLimite = (minimo: number, maximo: number): string => {
    const max = sanitizeMax(maximo);
    if (max === Number.POSITIVE_INFINITY) {
        return minimo === 0 ? 'Opcional' : `Min ${minimo}`;
    }
    return minimo === max ? `${max}` : `${minimo} - ${max}`;
};

const papelDisplay = (papel: string): string => {
    const mapa: Record<string, string> = {
        'TITULAR': 'Titulares',
        'TECNICO': 'Técnicos',
        'SECRETARIO': 'Secretários',
        'SUPLENTE': 'Suplentes'
    };
    return mapa[papel] || papel;
};

const papelEnumMap: Record<string, PapelNome> = {
    TITULAR: PapelNome.TITULAR,
    TECNICO: PapelNome.TECNICO,
    SECRETARIO: PapelNome.SECRETARIO,
    SUPLENTE: PapelNome.SUPLENTE
};

const normalizar = (valor: string) => valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const getBadgeVariant = (quantidade: number, minimo: number, maximo: number): string => {
    if (quantidade < minimo) {
        return 'secondary';
    }
    const limite = sanitizeMax(maximo);
    if (limite !== Number.POSITIVE_INFINITY && quantidade > limite) {
        return 'warning';
    }
    return 'success';
};

const pessoaMatchesBusca = (pessoa: Pessoa, termo: string) => {
    if (!termo) {
        return true;
    }
    const comparador = normalizar(termo);
    const nome = normalizar(pessoa.nome ?? '');
    const cpf = (pessoa.cpf ?? '').replace(/\D/g, '');
    const matricula = (pessoa.matricula ?? '').toLowerCase();
    return nome.includes(comparador) || cpf.includes(comparador) || matricula.includes(comparador);
};

const renderSelecionadosBadges = (selecionados: Pessoa[]) => {

    if (!selecionados.length) {
        return (
            <div>
                {/*
            <span className="text-muted small">Nenhum selecionado.</span>
            */}
            </div>
        );
    }
    return (
        <div className="d-flex flex-wrap gap-2">
            { /*
            <div>
            {selecionados.map(pessoa => (
                <Badge key={pessoa.id} bg="primary" className="text-truncate" style={{ maxWidth: '14rem' }}>
                    {pessoa.nome}
                </Badge>
            ))}
            */}
        </div>

    );

};

const ConsultaDisponibilidade: React.FC = () => {
    const { processoId, bancaId } = useParams<{ processoId: string; bancaId: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [loading, setLoading] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [pessoas, setPessoas] = useState<Pessoa[]>([]);
    const [pessoasPorSecoes, setPessoasPorSecoes] = useState<PessoasDisponibilidadeResponse | null>(null);
    const [composicaoTemplates, setComposicaoTemplates] = useState<ComposicaoTemplates | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [composicao, setComposicao] = useState<ComposicaoDisponibilidade | null>(null);
    const [selecoes, setSelecoes] = useState<SelecoesEstado>({ titulares: {}, papeis: {} });
    const [busca, setBusca] = useState('');
    const [filtroVinculo, setFiltroVinculo] = useState<string>('TODOS');
    const [activeStep, setActiveStep] = useState(0);
    const [showModalAjuda, setShowModalAjuda] = useState(false);
    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        if (!processoId || !bancaId) {
            return;
        }
        setBreadcrumbs(createBreadcrumbs.fromProcessos([
            { path: `/processos/${processoId}/bancas`, breadcrumb: 'Bancas' },
            { path: `/processos/${processoId}/bancas/${bancaId}/disponibilidade-banca`, breadcrumb: 'Consulta de Disponibilidade' }
        ]));
    }, [setBreadcrumbs, processoId, bancaId]);

    useEffect(() => {
        const carregar = async () => {
            if (!processoId || !bancaId) {
                return;
            }
            try {
                setLoading(true);
                const [composicaoResponse, pessoasSecoesResponse] = await Promise.all([
                    ComposicaoDisponibilidadeViewModel.getComposicaoDisponibilidade(processoId, bancaId)
                        .catch(error => {
                            // Erro 400 significa que não há templates criados ainda
                            if (error?.response?.status === 400) {
                                return null;
                            }
                            throw error;
                        }),
                    PessoaViewModel.getPessoasDisponibilidadePorSecoes(processoId, bancaId)
                ]);
                setComposicaoTemplates(composicaoResponse);
                
                // Selecionar automaticamente o primeiro template se houver apenas um
                if (composicaoResponse && composicaoResponse.templates.length === 1) {
                    const template = composicaoResponse.templates[0];
                    setSelectedTemplateId(template.id);
                    setComposicao(normalizarComposicao({
                        tipoBanca: composicaoResponse.tipoBanca,
                        categoriaEdital: composicaoResponse.categoriaEdital,
                        papeis: template.papeis
                    }));
                } else if (composicaoResponse && composicaoResponse.templates.length > 1) {
                    // Se há múltiplos templates, aguardar seleção do usuário
                    setSelectedTemplateId(null);
                    setComposicao(null);
                }
                setPessoasPorSecoes(pessoasSecoesResponse);
                // Manter compatibilidade com código existente
                const todasPessoas = [
                    ...pessoasSecoesResponse.pessoasParaTitulares,
                    ...pessoasSecoesResponse.pessoasParaSuplentes.filter(p => 
                        !pessoasSecoesResponse.pessoasParaTitulares.some(t => t.id === p.id)
                    ),
                    ...pessoasSecoesResponse.pessoasParaTecnicos.filter(p => 
                        !pessoasSecoesResponse.pessoasParaTitulares.some(t => t.id === p.id) &&
                        !pessoasSecoesResponse.pessoasParaSuplentes.some(s => s.id === p.id)
                    ),
                    ...pessoasSecoesResponse.pessoasParaSecretarios.filter(p => 
                        !pessoasSecoesResponse.pessoasParaTitulares.some(t => t.id === p.id) &&
                        !pessoasSecoesResponse.pessoasParaSuplentes.some(s => s.id === p.id) &&
                        !pessoasSecoesResponse.pessoasParaTecnicos.some(tc => tc.id === p.id)
                    )
                ];
                setPessoas(todasPessoas);
            } catch (error) {
                toast.error(BackendErrorMessage(error));
            } finally {
                setLoading(false);
            }
        };

        carregar();
    }, [processoId, bancaId]);

    // Efeito para atualizar composição quando template é selecionado
    useEffect(() => {
        if (!composicaoTemplates || !composicaoTemplates.templates || !selectedTemplateId) {
            return;
        }
        
        const templateSelecionado = composicaoTemplates.templates.find(t => t.id === selectedTemplateId);
        if (templateSelecionado) {
            setComposicao(normalizarComposicao({
                tipoBanca: composicaoTemplates.tipoBanca,
                categoriaEdital: composicaoTemplates.categoriaEdital,
                papeis: templateSelecionado.papeis
            }));
        }
    }, [composicaoTemplates, selectedTemplateId]);

    const handleTemplateSelect = (templateId: string) => {
        setSelectedTemplateId(templateId);
        // Limpar seleções anteriores ao trocar de template
        setSelecoes({ titulares: {}, papeis: {} });
    };

    useEffect(() => {
        if (!composicao) {
            return;
        }
        setSelecoes(prev => {
            const titularesBase: Record<string, Pessoa[]> = {};
            const titularConfig = composicao.papeis.find(p => p.papel === 'TITULAR');
            if (titularConfig) {
                titularConfig.vinculos!.forEach(vinculo => {
                    titularesBase[vinculo.vinculo] = prev.titulares[vinculo.vinculo] ?? [];
                });
            }

            const papeisBase: Record<string, Pessoa[]> = {};
            composicao.papeis
                .filter(p => p.papel !== 'TITULAR')
                .forEach(papel => {
                    papeisBase[papel.papel] = prev.papeis[papel.papel] ?? [];
                });

            return { titulares: titularesBase, papeis: papeisBase };
        });
    }, [composicao]);

    const titularConfig = useMemo(() => composicao?.papeis.find(p => p.papel === 'TITULAR') ?? null, [composicao]);

    const outrosPapeis = useMemo(() => {
        if (!composicao) {
            return [] as PapelComposicao[];
        }
        const ordem: Record<string, number> = { 
            'TECNICO': 1,
            'SECRETARIO': 2,
            'SUPLENTE': 3 
        };
        const papeisFiltrados = composicao.papeis.filter(p => p.papel !== 'TITULAR');
        return papeisFiltrados.sort((a, b) => {
            const left = ordem[a.papel] ?? 99;
            const right = ordem[b.papel] ?? 99;
            return left - right;
        });
    }, [composicao]);

    const vinculosDisponiveis = useMemo(() => {
        if (!pessoasPorSecoes) {
            return [];
        }
        const todasPessoas = [
            ...pessoasPorSecoes.pessoasParaTitulares,
            ...pessoasPorSecoes.pessoasParaSuplentes,
            ...pessoasPorSecoes.pessoasParaTecnicos,
            ...pessoasPorSecoes.pessoasParaSecretarios
        ];
        const valores = Array.from(new Set(todasPessoas.map(p => p.vinculo.nome)));
        valores.sort((a, b) => a.localeCompare(b));
        return valores;
    }, [pessoasPorSecoes]);

    const pessoaSelecionadaEmOutroTitular = useCallback((pessoaId: string, vinculoAtual: string) => {
        return Object.entries(selecoes.titulares).some(([vinculo, lista]) => vinculo !== vinculoAtual && lista.some(p => p.id === pessoaId));
    }, [selecoes.titulares]);

    // CORREÇÃO: Mantém os selecionados na lista com indicador visual
    const aplicaFiltros = useCallback((lista: Pessoa[], selecionados: Pessoa[], contextoVinculo?: string): Pessoa[] => {
        const filtrados = lista.filter(pessoa => {
            if (filtroVinculo !== 'TODOS') {
                if (contextoVinculo) {
                    if (filtroVinculo !== contextoVinculo) {
                        return false;
                    }
                } else if (pessoa.vinculo.nome !== filtroVinculo) {
                    return false;
                }
            }
            return pessoaMatchesBusca(pessoa, busca);
        });

        // Garantir que selecionados sempre apareçam na lista, mesmo se não passarem nos filtros
        const idsFiltrados = new Set(filtrados.map(p => p.id));
        const selecionadosFaltantes = selecionados.filter(sel => !idsFiltrados.has(sel.id));
        
        return [...filtrados, ...selecionadosFaltantes];
    }, [busca, filtroVinculo]);

    const titularesDisponiveis = useMemo(() => {
        if (!titularConfig || !pessoasPorSecoes) {
            return {} as Record<string, Pessoa[]>;
        }
        const resultado: Record<string, Pessoa[]> = {};
        titularConfig.vinculos!.forEach(vinculo => {
            const base = pessoasPorSecoes.pessoasParaTitulares
                .filter(p => p.vinculo.nome === vinculo.vinculo)
                .filter(p => !pessoaSelecionadaEmOutroTitular(p.id, vinculo.vinculo));
            const selecionados = selecoes.titulares[vinculo.vinculo] ?? [];
            resultado[vinculo.vinculo] = aplicaFiltros(base, selecionados, vinculo.vinculo);
        });
        return resultado;
    }, [aplicaFiltros, pessoasPorSecoes, selecoes.titulares, titularConfig, pessoaSelecionadaEmOutroTitular]);

    const papeisDisponiveis = useMemo(() => {
        if (!pessoasPorSecoes) {
            return [];
        }
        return outrosPapeis.map(papel => {
            const selecionados = selecoes.papeis[papel.papel] ?? [];
            const vinculosPermitidos = papel.vinculos?.map(v => v.vinculo) ?? [];
            
            let base: Pessoa[] = [];
            
            // Usar seção específica baseada no papel
            switch (papel.papel) {
                case 'SUPLENTE':
                    base = pessoasPorSecoes.pessoasParaSuplentes;
                    break;
                case 'TECNICO':
                    base = pessoasPorSecoes.pessoasParaTecnicos;
                    break;
                case 'SECRETARIO':
                    base = pessoasPorSecoes.pessoasParaSecretarios;
                    break;
                default:
                    base = pessoas; // fallback para papéis não mapeados
            }
            
            const baseFiltrada = base.filter(p => {
                if (vinculosPermitidos.length > 0 && !vinculosPermitidos.includes(p.vinculo.nome)) {
                    return false;
                }
                return true;
            });
            
            return {
                papel,
                lista: aplicaFiltros(baseFiltrada, selecionados),
                selecionados
            };
        });
    }, [aplicaFiltros, outrosPapeis, pessoasPorSecoes, pessoas, selecoes.papeis]);

    // Definir seções do stepper dinamicamente
    const stepperSections = useMemo(() => {
        const sections = ['Titulares'];

        if (composicao) {
            // Adicionar outros papéis dinamicamente na mesma ordem de outrosPapeis
            outrosPapeis.forEach(papel => {
                sections.push(papelDisplay(papel.papel));
            });
        }

        sections.push('Finalização');
        return sections;
    }, [composicao, outrosPapeis]);

    // Navegação entre seções
    const handleNext = () => {
        if (activeStep < stepperSections.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    const handleSectionClick = (index: number) => {
        setActiveStep(index);
    };

    const toggleTitular = (vinculo: VinculoQuantidade, pessoa: Pessoa) => {
        setSelecoes(prev => {
            const listaAtual = prev.titulares[vinculo.vinculo] ?? [];
            const jaSelecionado = listaAtual.some(p => p.id === pessoa.id);

            if (jaSelecionado) {
                return {
                    titulares: {
                        ...prev.titulares,
                        [vinculo.vinculo]: listaAtual.filter(p => p.id !== pessoa.id)
                    },
                    papeis: prev.papeis
                };
            }

            //const limite = sanitizeMax(vinculo.maximo);
            const limite = LIMITE_CONSULTA_POR_VINCULO;
            if (limite !== Number.POSITIVE_INFINITY && listaAtual.length >= limite) {
                toast.warning(`Limite de ${limite} titular(es) para ${vinculo.vinculo} atingido.`);
                return prev;
            }

            return {
                titulares: {
                    ...prev.titulares,
                    [vinculo.vinculo]: [...listaAtual, pessoa]
                },
                papeis: prev.papeis
            };
        });
    };

    const togglePapel = (papel: PapelComposicao, pessoa: Pessoa) => {
        setSelecoes(prev => {
            const listaAtual = prev.papeis[papel.papel] ?? [];
            const jaSelecionado = listaAtual.some(p => p.id === pessoa.id);

            if (jaSelecionado) {
                return {
                    titulares: prev.titulares,
                    papeis: {
                        ...prev.papeis,
                        [papel.papel]: listaAtual.filter(p => p.id !== pessoa.id)
                    }
                };
            }

            //const limite = sanitizeMax(papel.maximo);
            const limite = LIMITE_CONSULTA_POR_VINCULO;
            if (limite !== Number.POSITIVE_INFINITY && listaAtual.length >= limite) {
                toast.warning(`Limite de ${limite} ${papelDisplay(papel.papel).toLowerCase()} atingido.`);
                return prev;
            }

            return {
                titulares: prev.titulares,
                papeis: {
                    ...prev.papeis,
                    [papel.papel]: [...listaAtual, pessoa]
                }
            };
        });
    };

    const validarSelecoes = (): boolean => {
        if (!composicao) {
            toast.error('Composição não carregada.');
            return false;
        }

        for (const papel of composicao.papeis) {
            if (papel.papel === 'TITULAR') {
                const selecionadosPorVinculo = selecoes.titulares;
                for (const vinculo of papel.vinculos!) {
                    const quantidade = selecionadosPorVinculo[vinculo.vinculo]?.length ?? 0;
                    if (quantidade < vinculo.minimo) {
                        //toast.error(`Selecione ${vinculo.minimo} titular(es) para ${vinculo.vinculo}.`);
                        return false;
                    }
                    //const limite = sanitizeMax(vinculo.maximo);
                    const limite = LIMITE_CONSULTA_POR_VINCULO;
                    if (limite !== Number.POSITIVE_INFINITY && quantidade > limite) {
                        //toast.error(`Máximo de ${limite} titular(es) para ${vinculo.vinculo}.`);
                        return false;
                    }
                }
            } else {
                const quantidade = selecoes.papeis[papel.papel]?.length ?? 0;
                if (quantidade < papel.minimo) {
                    //toast.error(`Selecione pelo menos ${papel.minimo} ${papelDisplay(papel.papel).toLowerCase()}.`);
                    return false;
                }
                //const limite = sanitizeMax(papel.maximo);
                const limite = LIMITE_CONSULTA_POR_VINCULO;
                if (limite !== Number.POSITIVE_INFINITY && quantidade > limite) {
                    //toast.error(`Máximo de ${limite} ${papelDisplay(papel.papel).toLowerCase()}.`);
                    return false;
                }
            }
        }
        return true;
    };

    const handleEnviar = async () => {
        if (!processoId || !bancaId || !composicao) {
            toast.error('Informações insuficientes para enviar a consulta.');
            return;
        }

        // Consulta não precisa.
        /*
        if (!validarSelecoes()) {
            return;
        }
        */

        setEnviando(true);
        try {
            const payload: { pessoa: Pessoa; papelMembro: PapelNome }[] = [];

            Object.entries(selecoes.titulares).forEach(([, lista]) => {
                lista.forEach(pessoa => payload.push({ pessoa, papelMembro: PapelNome.TITULAR }));
            });
            Object.entries(selecoes.papeis).forEach(([papel, lista]) => {
                const papelEnum = papelEnumMap[papel];
                if (!papelEnum) {
                    return;
                }
                lista.forEach(pessoa => payload.push({ pessoa, papelMembro: papelEnum }));
            });

            await MembrosBancaViewModel.enviarConsultaDisponibilidade(
                processoId,
                bancaId,
                payload,
                composicao.categoriaEdital as CategoriaEdital
            );

            toast.success('Consulta de disponibilidade enviada.');
            navigate(`/processos/${processoId}/bancas`);
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setEnviando(false);
        }
    };

    // Calcular o progresso do stepper
    const calcularProgresso = useCallback(() => {
        if (!composicao) return 0;

        let totalRequisitos = 0;
        let completos = 0;

        for (const papel of composicao.papeis) {
            if (papel.papel === 'TITULAR') {
                for (const vinculo of papel.vinculos!) {
                    totalRequisitos++;
                    const quantidade = (selecoes.titulares[vinculo.vinculo]?.length ?? 0);
                    if (quantidade >= vinculo.minimo) {
                        completos++;
                    }
                }
            } else {
                totalRequisitos++;
                const quantidade = (selecoes.papeis[papel.papel]?.length ?? 0);
                if (quantidade >= papel.minimo) {
                    completos++;
                }
            }
        }

        return Math.round((completos / totalRequisitos) * 100);
    }, [composicao, selecoes]);

    const progresso = calcularProgresso();

    // CORREÇÃO: Função renderLista atualizada para mostrar seleção visual
    const renderLista = (lista: Pessoa[], selecionados: Pessoa[], onToggle: (pessoa: Pessoa) => void, vazio: string) => (
        <ListGroup className="shadow-sm" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {lista.length === 0 ? (
                <ListGroup.Item className="text-muted fst-italic">{vazio}</ListGroup.Item>
            ) : (
                lista.map(pessoa => {
                    const isSelecionado = selecionados.some(sel => sel.id === pessoa.id);
                    const jaDefinido = pessoa.jaDefinidoMesmoEditalChamada;
                    const jaConsultado = pessoa.jaConsultadoMesmoEditalChamada;
                    const isDesabilitado = jaDefinido;
                    
                    return (
                        <ListGroup.Item
    key={pessoa.id}
    action={!isDesabilitado}
    className={`d-flex justify-content-between align-items-start ${
        isSelecionado ? 'bg-light border-primary' : ''
    } ${
        isDesabilitado ? 'bg-light text-muted' : ''
    }`}
    onClick={() => !isDesabilitado && onToggle(pessoa)}
    style={{
        cursor: isDesabilitado ? 'not-allowed' : 'pointer',
        borderLeft: isSelecionado ? '4px solid #0d6efd' : 
                   jaDefinido ? '4px solid #dc3545' :
                   jaConsultado ? '4px solid #ffc107' : 
                   '4px solid transparent',
        transition: 'all 0.2s ease',
        opacity: isDesabilitado ? 0.6 : 1
    }}
>
    <div className="flex-grow-1">
        <div className="fw-semibold d-flex align-items-center gap-2">
            {pessoa.nome}                                    
            {jaDefinido && (
                <Badge 
                    bg="danger" 
                    className="ms-2"
                    title="Já foi definido como membro no mesmo Edital e Chamada, em outra banca (de outro tipo), pode bloquear a seleção na etapa de definição"
                >
                    Já Definido
                </Badge>
            )}
            {jaConsultado && !jaDefinido && (
                <Badge 
                    bg="warning" 
                    text="dark" 
                    className="ms-2"
                    title="Já foi consultado anteriormente no mesmo Edital e Chamada, em outra banca (de outro tipo), pode bloquear a seleção na etapa de definição"
                >
                    Já Consultado
                </Badge>
            )}
            {!pessoa.isMembroCavu && (
                <Badge 
                    bg="secondary" 
                    text="white"
                    title="Pessoa externa à instituição"
                >
                    Externo
                </Badge>
            )}
        </div>
        <small className="text-muted">
            {pessoa.vinculo.nome}
            {pessoa.matricula ? ` · ${pessoa.matricula}` : ''}
        </small>
    </div>
    {isSelecionado && !isDesabilitado && (
        <div className="text-primary">
            <i className="bi bi-check-circle-fill m-3"></i>
        </div>
    )}                            
</ListGroup.Item>
                    );
                })
            )}
        </ListGroup>
    );

    // Renderizar conteúdo baseado na seção ativa
    const renderSectionContent = () => {
        switch (activeStep) {
            case 0: // Titulares
                return renderTitularesSection();

            case stepperSections.length - 1: // Finalização
                return renderFinalizacaoSection();

            default: // Outros papéis
                return renderPapelSection(activeStep - 1);
        }
    };

    const renderTitularesSection = () => {
        if (!titularConfig) return null;

        return (
            <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Titulares</span>
                    <Badge bg={getBadgeVariant(
                        Object.values(selecoes.titulares).reduce((acumulado, lista) => acumulado + lista.length, 0),
                        titularConfig.minimo,
                        titularConfig.maximo
                    )}>
                        {Object.values(selecoes.titulares).reduce((acumulado, lista) => acumulado + lista.length, 0)} / {formatLimite(titularConfig.minimo, titularConfig.maximo)}
                    </Badge>
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        {renderSelecionadosBadges(Object.values(selecoes.titulares).flat())}
                    </div>

                    <div className="d-flex flex-nowrap overflow-x-auto pb-2 gap-3">
                        {titularConfig.vinculos!
                            .sort((a, b) => {
                                if (a.vinculo.toLowerCase().includes('docente')) return -1;
                                if (b.vinculo.toLowerCase().includes('docente')) return 1;
                                return 0;
                            })
                            .map(vinculo => (
                            <div
                                key={vinculo.vinculo}
                                className="flex-shrink-0"
                                style={{ minWidth: '280px', maxWidth: '320px' }}
                            >
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Header className="bg-transparent px-2 py-1">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-medium">{vinculo.vinculo}</span>
                                            <Badge bg={getBadgeVariant(
                                                selecoes.titulares[vinculo.vinculo]?.length ?? 0,
                                                vinculo.minimo,
                                                vinculo.maximo
                                            )}>
                                                {(selecoes.titulares[vinculo.vinculo]?.length ?? 0)} / {formatLimite(vinculo.minimo, vinculo.maximo)}
                                            </Badge>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="px-2 pt-1 pb-2">
                                        {renderLista(
                                            titularesDisponiveis[vinculo.vinculo] ?? [],
                                            selecoes.titulares[vinculo.vinculo] ?? [],
                                            pessoa => toggleTitular(vinculo, pessoa),
                                            'Nenhuma pessoa disponível para este vínculo.'
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderPapelSection = (papelIndex: number) => {
        const papelItem = papeisDisponiveis[papelIndex];
        if (!papelItem) return null;

        const { papel, lista, selecionados } = papelItem;

        return (
            <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">{papelDisplay(papel.papel)}</span>
                    <Badge bg={getBadgeVariant(selecionados.length, papel.minimo, papel.maximo)}>
                        {selecionados.length} / {formatLimite(papel.minimo, papel.maximo)}
                    </Badge>
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        {renderSelecionadosBadges(selecionados)}
                    </div>
                    {renderLista(
                        lista,
                        selecionados,
                        pessoa => togglePapel(papel, pessoa),
                        `Nenhuma pessoa disponível para ${papelDisplay(papel.papel).toLowerCase()}.`
                    )}
                </Card.Body>
            </Card>
        );
    };

    const renderFinalizacaoSection = () => {
        const totalMembros = Object.values(selecoes.titulares).flat().length +
            Object.values(selecoes.papeis).flat().length;

        const tipoBancaNome = composicao?.tipoBanca?.nome as TipoBancaNome | undefined;
        const categoria = composicao?.categoriaEdital as CategoriaEdital;

        return (
            <Card className="shadow-sm">
                <Card.Header className="bg-light">
                    <h6 className="mb-0">Resumo da Consulta</h6>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <h6>Composição Selecionada</h6>
                            <ListGroup variant="flush">
                                <ListGroup.Item className="px-0">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-semibold">Titulares:</span>
                                        <Badge bg="primary">
                                            {Object.values(selecoes.titulares).flat().length}
                                        </Badge>
                                    </div>
                                    {Object.entries(selecoes.titulares).map(([vinculo, pessoas]) => (
                                        pessoas.length > 0 && (
                                            <div key={vinculo} className="mb-2">
                                                <small className="text-muted fw-medium">{vinculo}:</small>
                                                <div className="ms-3">
                                                    {pessoas.map(pessoa => (
                                                        <div key={pessoa.id} className="small">
                                                            • {pessoa.nome}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </ListGroup.Item>
                                {papeisDisponiveis.map(({ papel, selecionados }) => (
                                    <ListGroup.Item key={papel.papel} className="px-0">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-semibold">{papelDisplay(papel.papel)}:</span>
                                            <Badge bg="secondary">{selecionados.length}</Badge>
                                        </div>
                                        {selecionados.length > 0 && (
                                            <div className="ms-3">
                                                {selecionados.map(pessoa => (
                                                    <div key={pessoa.id} className="small">
                                                        • {pessoa.nome}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </ListGroup.Item>
                                ))}
                                <ListGroup.Item className="d-flex justify-content-between px-0 fw-bold">
                                    <span>Total de membros:</span>
                                    <Badge bg="success">{totalMembros}</Badge>
                                </ListGroup.Item>
                            </ListGroup>
                        </Col>
                        <Col md={6}>
                            <h6>Informações da Banca</h6>
                            <div className="small">
                                <div><strong>Tipo:</strong> {tipoBancaNome || 'Não informado'}</div>
                                <div><strong>Categoria:</strong> {formatarCategoria(categoria)}</div>
                                <div><strong>Status da Seleção:</strong> {validarSelecoes() ?
                                    <Badge bg="success">Satisfaz composição mínima de membros para este Edital</Badge> :
                                    <Badge bg="warning">Composição mínima para esta Categoria de Edital não atingida</Badge>}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" />
            </div>
        );
    }



    // Verificar se não há composições disponíveis
    if (!composicaoTemplates || (composicaoTemplates.templates && composicaoTemplates.templates.length === 0)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center">
                    <h5>Nenhuma composição disponível</h5>
                    <p className="text-muted mb-3">Não há composições cadastradas para este tipo de banca.</p>
                    <Button 
                        variant="primary" 
                        onClick={() => navigate('/composicoes/novo')}
                    >
                        Criar Composição
                    </Button>
                </div>
            </div>
        );
    }

    // Se há múltiplos templates e nenhum foi selecionado, mostrar apenas o seletor
    if (composicaoTemplates && composicaoTemplates.templates && composicaoTemplates.templates.length > 1 && !selectedTemplateId) {
        return (
            <Container fluid className="py-4">
                <TemplateSelector
                    templates={composicaoTemplates.templates}
                    selectedTemplateId={selectedTemplateId}
                    onTemplateSelect={handleTemplateSelect}
                />
            </Container>
        );
    }

    if (!composicao) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <Container fluid className="py-4" style={{ position: 'relative' }}>
            <Joyride
                steps={[
                    {
                        target: '[data-tour="stepper"]',
                        content: 'O stepper mostra as etapas da consulta. Clique em qualquer etapa para navegar diretamente. A barra de progresso indica o preenchimento mínimo.',
                        disableBeacon: true,
                    },
                    {
                        target: '[data-tour="filtros"]',
                        content: 'Use os filtros para buscar pessoas por nome, CPF, matrícula ou vínculo. Os filtros se aplicam à lista da etapa atual.',
                    },
                    {
                        target: '[data-tour="conteudo"]',
                        content: 'Selecione as pessoas clicando nelas. Pessoas com borda azul já estão selecionadas. Badges "Já Definido" ou "Já Consultado" indicam que a pessoa já participa de outra banca do mesmo edital.',
                    },
                    {
                        target: '[data-tour="navegacao"]',
                        content: 'Use os botões para avançar entre as etapas. Na última etapa (Finalização), você verá o resumo e poderá enviar a consulta de disponibilidade.',
                    },
                ]}
                run={runTour}
                continuous
                showSkipButton
                showProgress
                scrollToFirstStep
                callback={(data: CallBackProps) => {
                    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) setRunTour(false);
                }}
                locale={{ back: 'Voltar', close: 'Fechar', last: 'Finalizar', next: 'Próximo', skip: 'Pular tour' }}
                styles={{ options: { primaryColor: '#c00', zIndex: 10000 } }}
            />
            <div style={{ position: 'absolute', top: '-48px', right: '20px', display: 'flex', gap: '8px', zIndex: 1000 }}>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setRunTour(true)}
                    title="Tour guiado"
                >
                    <i className="bi bi-signpost-split me-1" /> Tour
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowModalAjuda(true)}
                    title="Ajuda"
                >
                    <i className="bi bi-question-circle me-1" /> Ajuda
                </Button>
            </div>

            {/* Seletor de Template (se houver múltiplos) */}
            {composicaoTemplates && composicaoTemplates.templates && composicaoTemplates.templates.length > 1 && (
                <TemplateSelector
                    templates={composicaoTemplates.templates}
                    selectedTemplateId={selectedTemplateId}
                    onTemplateSelect={handleTemplateSelect}
                />
            )}

            {/* Stepper Moderno */}
            <div data-tour="stepper">
            <ModernStepper
                sections={stepperSections}
                activeSection={activeStep}
                onSectionClick={handleSectionClick}
                progresso={progresso}
            />
            </div>

            {/* Filtros (mostrar apenas nas seções de seleção) */}
            {activeStep < stepperSections.length - 1 && (
                <Row data-tour="filtros" className="g-3 align-items-end mb-4">
                    <Col md={6} lg={4}>
                        <Form.Group controlId="busca">
                            <Form.Label>Buscar por nome, CPF ou matrícula</Form.Label>
                            <Form.Control
                                type="text"
                                value={busca}
                                placeholder="Digite para filtrar pessoas"
                                onChange={event => setBusca(event.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} lg={3}>
                        <Form.Group controlId="filtroVinculo">
                            <Form.Label>Filtrar por vínculo</Form.Label>
                            <Form.Select value={filtroVinculo} onChange={event => setFiltroVinculo(event.target.value)}>
                                <option value="TODOS">Todos os vínculos</option>
                                {vinculosDisponiveis.map(vinculo => (
                                    <option key={vinculo} value={vinculo}>{vinculo}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            )}

            {/* Conteúdo da seção atual */}
            <div data-tour="conteudo" className="mb-4">
                {renderSectionContent()}
            </div>

            {/* Navegação */}
            <div data-tour="navegacao">
            <StepperNavigation
                activeStep={activeStep}
                totalSteps={stepperSections.length}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onSubmit={handleEnviar}
                isSubmitting={enviando}
                isValid={validarSelecoes()}
                showSubmit={activeStep === stepperSections.length - 1}
            />
            </div>

            <Modal show={showModalAjuda} onHide={() => setShowModalAjuda(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ajuda - Consulta de Disponibilidade</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Esta página é usada para consultar a disponibilidade de pessoas para compor a banca, na data escolhida para ela.
                    </p>
                    <p>
                        Ao finalizar o procedimento, as pessoas selecionadas receberão o email de consulta de disponibilidade e a Banca muda de Status para CONSULTADA.
                    </p>
                    <p className="mb-2">
                        <strong>Critérios de seleção:</strong>
                    </p>
                    <ul className="mb-0">
                        <li>A seleção é flexível em relação a quantidades</li>
                        <li>Os papéis e vínculos são os definidos previamente pela Composição de Banca correspondente</li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModalAjuda(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ConsultaDisponibilidade;